import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@repo/ui/components/avatar';
import { Button } from '@repo/ui/components/button';
import { Separator } from '@repo/ui/components/separator';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Calendar, ChevronRight, Plus } from 'lucide-react';

import { ExperienceBadge } from '@/components/experience-badge';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageContainer,
} from '@/components/shared';
import { useRelationship } from '@/hooks/queries/use-relationship';
import { useUserMe } from '@/hooks/queries/use-user-me';

import { CountdownCard } from './-components/countdown-card';

export const Route = createFileRoute('/(app)/home/')({
  component: HomePage,
});

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) {
    return 'Bom dia';
  }
  if (hour < 18) {
    return 'Boa tarde';
  }
  return 'Boa noite';
}

function getInitials(name: string | null): string {
  if (!name) {
    return '?';
  }
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function HomePage() {
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useUserMe();
  const relationshipId = userData?.relationships?.[0]?.id;

  const {
    data: relationshipData,
    isLoading: isLoadingRelationship,
    error: relationshipError,
    refetch,
  } = useRelationship(relationshipId);

  const isLoading = isLoadingUser || isLoadingRelationship;
  const error = userError || relationshipError;

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState message="Carregando..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState
          title="Erro ao carregar dados"
          message="Não foi possível carregar suas informações. Tente novamente."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  const user = userData?.user;
  const firstName = user?.name?.split(' ')[0] ?? 'usuário';
  const occasions = relationshipData?.user_occasions ?? [];

  return (
    <PageContainer>
      <div className="mx-auto w-full max-w-md space-y-6">
        {/* Greeting */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm">{getGreeting()},</p>
            <h1 className="text-xl font-semibold">{firstName} 👋</h1>
          </div>
          <Link to="/account">
            <Avatar className="h-12 w-12 cursor-pointer transition-opacity hover:opacity-80">
              <AvatarImage
                src={user?.avatar_url ?? undefined}
                alt={user?.name ?? 'Avatar'}
              />
              <AvatarFallback>{getInitials(user?.name ?? null)}</AvatarFallback>
            </Avatar>
          </Link>
        </div>

        <Separator className="my-8" />

        {/* Próxima ocasião section */}
        {occasions.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">Próxima ocasião</h2>
              <Link
                to="/occasions/me"
                className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
              >
                ver todas
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <CountdownCard occasions={occasions} />
          </div>
        ) : (
          <EmptyState
            icon={<Calendar className="h-6 w-6" />}
            title="Nenhuma ocasião cadastrada"
            description="Adicione suas datas especiais para ver a contagem regressiva."
            action={
              <Button asChild>
                <Link to="/occasions/me">
                  <Plus className="mr-2 h-4 w-4" />
                  Criar primeira ocasião
                </Link>
              </Button>
            }
          />
        )}
      </div>

      {/* Fixed bottom */}
      <div className="fixed right-0 bottom-20 left-0 flex justify-center px-4 md:bottom-6 md:left-64">
        <Button variant="outline" className="pointer-events-none rounded-full">
          <ExperienceBadge />
        </Button>
      </div>
    </PageContainer>
  );
}
